/**
 * Money — renders an already-formatted currency / number string with the VALUE
 * digits kept prominent and the secondary glyphs (currency symbol, %, unit words,
 * separators) de-emphasised, so the amount reads first. The magnitude suffix
 * (K / M) stays attached to the number.
 *
 * It is purely presentational — it never re-formats or rounds the value, it only
 * wraps the parts in spans the stylesheet fades (`.rs-money__unit`).
 *
 *   "2,295,000 €"            → 2,295,000 + faded " €"
 *   "1.25M € - 2.5M €"       → 1.25M + faded " € - " + 2.5M + faded " €"
 *
 * Props:
 *   - children {string|number} — the formatted string (e.g. offer.priceLabel)
 */
import React from 'react';

// A "value" token: digits with thousands/decimal separators plus an optional
// k/m magnitude suffix ("2,295,000", "1.25m"). Everything the split leaves
// between these tokens (currency symbols, %, spaces, dashes) is secondary.
const VALUE = /([0-9][0-9.,]*[KkMm]?)/;
// A value that ends in a k/m abbreviator — captured so the suffix can be shown
// smaller than the number it scales.
const ABBR = /^([0-9][0-9.,]*)([KkMm])$/;

// Render a value token, splitting off a trailing k/m so it can be sized down.
const renderValue = (part, key) => {
    const m = part.match(ABBR);
    if (!m) return <span key={key} className="rs-money__val">{part}</span>;
    return (
        <span key={key} className="rs-money__val">
            {m[1]}<span className="rs-money__abbr">{m[2]}</span>
        </span>
    );
};

export default function Money({ children }) {
    const text = children == null ? '' : String(children);
    const parts = text.split(VALUE).filter(Boolean);
    return (
        <>
            {parts.map((part, i) => (VALUE.test(part)
                ? renderValue(part, i)
                : <span key={i} className="rs-money__unit">{part}</span>
            ))}
        </>
    );
}
